import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import { Check } from 'lucide-react';
import { addDays, startOfDay, sameDay, ymd, laneAllDay, isAllDayLike, fmtTime, weekdayName, shortMonth } from './calendarMath';
import { tint } from './palette';

// Month view: six weeks, all-day bars spanning their days, timed appointments as dotted rows.
// Click a day's empty space to book on it; drag an appointment to another day (same time).
// A day with more than fits shows "N more", which opens that day's full list.
const LANE_PX = 22;

export default function MonthGrid({ grid, anchor, occs, colorFor, onSelect, onCreate, onChange, onDayClick, onMore, now, selectedKey }) {
  const weeks = useMemo(() => Array.from({ length: 6 }, (_, w) => grid.slice(w * 7, w * 7 + 7)), [grid]);
  const [over, setOver] = useState(null);
  const drag = useRef(null);
  const bodyRef = useRef(null);
  const today = now || new Date();
  // As many rows of appointments as the week's height holds (Google does the same).
  const [MAX_LANES, setMaxLanes] = useState(3);
  useLayoutEffect(() => {
    const el = bodyRef.current;
    if (!el) return undefined;
    const measure = () => setMaxLanes(Math.max(2, Math.floor((el.clientHeight / 6 - 30) / LANE_PX)));
    measure();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    if (ro) ro.observe(el);
    return () => ro && ro.disconnect();
  }, []);

  const rows = useMemo(() => weeks.map((days) => {
    const ws = startOfDay(days[0]);
    const we = addDays(ws, 7);
    const inWeek = occs.filter((o) => o.start < we && o.end > ws);
    const bars = laneAllDay(inWeek.filter(isAllDayLike), days);
    // Timed ones fill the free lanes of their own day, in time order.
    const used = days.map((_, i) => new Set(bars.filter((b) => b.startIdx <= i && b.endIdx >= i).map((b) => b.lane)));
    const chips = [];
    inWeek.filter((o) => !isAllDayLike(o)).forEach((o) => {
      const i = Math.round((startOfDay(o.start) - ws) / 86400000);
      if (i < 0 || i > 6) return;
      let lane = 0;
      while (used[i].has(lane)) lane++;
      used[i].add(lane);
      chips.push({ ...o, dayIdx: i, lane });
    });
    const count = days.map((_, i) => used[i].size);
    return { days, bars, chips, count };
  }), [weeks, occs]);

  // Drag an appointment to a day: find the day cell under the pointer.
  const down = (e, occ) => {
    e.stopPropagation();
    if (e.button !== 0) return;
    drag.current = { occ, x0: e.clientX, y0: e.clientY, moved: false, touch: e.pointerType === 'touch', el: e.currentTarget };
    if (!occ.readOnly && e.pointerType !== 'touch') e.currentTarget.setPointerCapture?.(e.pointerId);
  };
  const move = (e) => {
    const d = drag.current;
    if (!d || d.touch || d.occ.readOnly) return;
    if (!d.moved && Math.hypot(e.clientX - d.x0, e.clientY - d.y0) < 4) return;
    d.moved = true;
    const cell = document.elementsFromPoint(e.clientX, e.clientY).find((x) => x.dataset && x.dataset.date);
    d.target = cell ? cell.dataset.date : null;
    setOver(d.target);
  };
  const up = () => {
    const d = drag.current;
    drag.current = null;
    setOver(null);
    if (!d) return;
    if (!d.moved) { onSelect(d.occ, d.el.getBoundingClientRect()); return; }
    if (!d.target || d.target === ymd(d.occ.start)) return;
    const [y, m, dd] = d.target.split('-').map(Number);
    const shift = Math.round((new Date(y, m - 1, dd) - startOfDay(d.occ.start)) / 86400000);
    onChange(d.occ, addDays(d.occ.start, shift), addDays(d.occ.end, shift));
  };

  return (
    <div className="flex flex-col h-full min-h-0 bg-white" onPointerMove={move} onPointerUp={up} onPointerCancel={() => { drag.current = null; setOver(null); }}>
      <div className="grid grid-cols-7 border-b border-slate-200">
        {[1, 2, 3, 4, 5, 6, 0].map((d) => (
          <div key={d} className="py-2 text-center text-[11px] font-semibold tracking-[0.06em] uppercase text-slate-500">
            <span className="hidden sm:inline">{weekdayName(d).slice(0, 3)}</span><span className="sm:hidden">{weekdayName(d).slice(0, 1)}</span>
          </div>
        ))}
      </div>
      <div ref={bodyRef} className="flex-1 min-h-0 grid grid-rows-6">
        {rows.map((row, w) => (
          <div key={w} className="relative grid grid-cols-7 border-b border-slate-200 last:border-b-0 min-h-[84px]">
            {row.days.map((d, i) => {
              const out = d.getMonth() !== anchor.getMonth();
              const isToday = sameDay(d, today);
              const hidden = Math.max(0, row.count[i] - MAX_LANES);
              const date = ymd(d);
              return (
                <div key={date} data-date={date}
                  className={clsx('relative border-l border-slate-200 first:border-l-0 pt-7 cursor-pointer transition-colors',
                    out ? 'bg-slate-50/60' : 'bg-white', over === date ? 'bg-indigo-50' : 'hover:bg-slate-50/80')}
                  onClick={(e) => { if (e.target === e.currentTarget) onCreate({ start: startOfDay(d), end: addDays(startOfDay(d), 1), allDay: false, fromMonth: true }, e.currentTarget.getBoundingClientRect()); }}>
                  <button type="button" onClick={() => onDayClick(d)}
                    aria-label={`${weekdayName(d.getDay())} ${d.getDate()} ${shortMonth(d.getMonth())}`}
                    className={clsx('absolute top-1 left-1/2 -translate-x-1/2 min-w-[26px] h-[26px] px-1 rounded-full text-[12px] font-semibold tabular-nums focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500',
                      isToday ? 'bg-indigo-600 text-white' : out ? 'text-slate-400 hover:bg-slate-200/70' : 'text-slate-700 hover:bg-slate-100')}>
                    {d.getDate() === 1 ? `${d.getDate()} ${shortMonth(d.getMonth())}` : d.getDate()}
                  </button>
                  {hidden > 0 && (
                    <button type="button" onClick={(e) => onMore(d, e.currentTarget.getBoundingClientRect())}
                      className="absolute left-1 right-1 text-left text-[11.5px] font-semibold text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded px-1.5"
                      style={{ top: 28 + (MAX_LANES - 1) * LANE_PX }}>
                      {hidden + 1} more
                    </button>
                  )}
                </div>
              );
            })}
            {row.bars.filter((b) => b.lane < MAX_LANES - (row.count.some((c, i) => c > MAX_LANES && b.startIdx <= i && b.endIdx >= i) ? 1 : 0)).map((b) => {
              const c = colorFor(b);
              const key = b.kind === 'keydate';
              return (
                <button key={`${b.key}-${w}`} type="button" data-occ onPointerDown={(e) => down(e, b)}
                  onClick={(e) => { if (e.detail === 0) onSelect(b, e.currentTarget.getBoundingClientRect()); }}
                  aria-label={`${b.title}${b.participant ? `, ${b.participant}` : ''}`}
                  className={clsx('absolute h-[20px] rounded px-1.5 text-[11.5px] font-semibold truncate text-left flex items-center gap-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500',
                    selectedKey === b.key && 'shadow-md')}
                  style={{ top: 28 + b.lane * LANE_PX, left: `calc(${(b.startIdx / 7) * 100}% + 3px)`, width: `calc(${((b.endIdx - b.startIdx + 1) / 7) * 100}% - 6px)`,
                    background: key ? tint(c.hex, 0.13) : c.hex, color: key ? c.hex : c.ink }}>
                  {b.icon && <b.icon size={11} aria-hidden className="flex-shrink-0" />}
                  <span className={clsx('truncate', b.status === 'cancelled' && 'line-through')}>{b.title}{key && b.participant ? ` · ${b.participant}` : ''}</span>
                </button>
              );
            })}
            {row.chips.filter((ch) => ch.lane < MAX_LANES - (row.count[ch.dayIdx] > MAX_LANES ? 1 : 0)).map((ch) => {
              const c = colorFor(ch);
              const past = ch.end < today;
              return (
                <button key={ch.key} type="button" data-occ onPointerDown={(e) => down(e, ch)}
                  onClick={(e) => { if (e.detail === 0) onSelect(ch, e.currentTarget.getBoundingClientRect()); }}
                  aria-label={`${ch.title}, ${fmtTime(ch.start)}${ch.participant ? `, ${ch.participant}` : ''}`}
                  className={clsx('absolute h-[20px] rounded px-1 text-[11.5px] truncate text-left flex items-center gap-1 hover:bg-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500',
                    selectedKey === ch.key && 'bg-slate-100', past && 'opacity-60', ch.status === 'cancelled' && 'line-through text-slate-400')}
                  style={{ top: 28 + ch.lane * LANE_PX, left: `calc(${(ch.dayIdx / 7) * 100}% + 3px)`, width: `calc(${100 / 7}% - 6px)` }}>
                  {ch.status === 'completed'
                    ? <Check size={11} style={{ color: c.hex }} className="flex-shrink-0" aria-hidden />
                    : <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: c.hex }} aria-hidden />}
                  <span className="hidden sm:inline text-slate-500 tabular-nums flex-shrink-0">{fmtTime(ch.start)}</span>
                  <span className="font-semibold text-slate-800 truncate">{ch.title}</span>
                </button>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}
